import React, { useEffect } from 'react';
import { ConfigProvider, message, Typography, type ThemeConfig } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import { genericErrorText, statusText, networkErrorText } from '../i18n/serverErrors';
import { tokens } from '../theme/tokens';

/**
 * THE one notification channel. Every toast in the app - our own calls, antd's static
 * `message.*` / `notification.*` (re-routed by installNotifyBridge in main.tsx) and the axios
 * interceptor - renders as an antd message through the single <NotifyHost /> in App.tsx, so it
 * follows the antd theme, dark mode and RTL, and a failure never shows as two popups in two corners.
 * There is no other toast library and no toast CSS of our own.
 *
 *   notify.success(t('x:saved'));
 *   notify.error(t('x:saveFailed'), { description: err.message });
 *
 * - Identical text while still visible updates the one toast instead of stacking another.
 * - Right after the interceptor has reported a server or network error, a caller's own
 *   follow-up error ("Failed to load X") is dropped: the user has already been told.
 * - Technical text (exception names, stack lines, "Request failed with status code 500",
 *   provider/config details) is never shown; a plain message takes its place.
 */
export type NotifyKind = 'success' | 'error' | 'info' | 'warning' | 'loading';

export interface NotifyOptions {
  /** Same id = the same toast (replaced in place). Defaults to kind + text. */
  id?: string;
  description?: React.ReactNode;
  /** Milliseconds; 0 keeps it until dismissed. */
  duration?: number;
  /** Replaces the antd icon for this kind. */
  icon?: React.ReactNode;
}

const DEFAULT_MS: Record<NotifyKind, number> = { success: 3000, info: 3500, warning: 4500, error: 4500, loading: Infinity };

/** Text a person cannot act on: implementation detail that leaked from a server or library. */
const TECHNICAL = [
  /exception\b/i,
  /\bat [\w$.<>]+\(/,                     // stack frame
  /object reference not set/i,
  /cannot read propert/i,
  /is not a function/i,
  /\[object object\]/i,
  /^(undefined|null|nan)$/i,
  /\bsql\b|sqlstate|constraint ['"]?\w+['"]? |duplicate key/i,
  /api[\s_-]?key|secret|token is invalid|invalid_grant|unauthorized_client/i,
  /econn(refused|reset|aborted)|enotfound|socket hang up/i,
  /<\s*(html|!doctype|body)\b/i,          // gateway HTML page
  /\bfiles? (is |are )?not (correct|valid)\b/i,
  /\b(stack ?trace|traceid|correlation[- ]?id)\b/i,
];

export const isTechnicalMessage = (text: string | null | undefined): boolean =>
  !!text && TECHNICAL.some((re) => re.test(text));

/** Axios' own wording, mapped to ours. */
const libraryText = (text: string): string | null => {
  const m = /^request failed with status code (\d{3})$/i.exec(text.trim());
  if (m) { const s = Number(m[1]); return statusText(s) ?? genericErrorText(s); }
  if (/^network error$/i.test(text.trim())) return networkErrorText('unreachable').title;
  if (/^timeout of \d+ms exceeded$/i.test(text.trim())) return networkErrorText('timeout').title;
  return null;
};

/** What an error toast may say: the text itself when it is meant for people, ours otherwise. */
export const userFacingError = (text: string): string =>
  libraryText(text) ?? (isTechnicalMessage(text) ? genericErrorText(0) : text);

let serverErrorAt = 0;
const SERVER_ERROR_QUIET_MS = 1500;
/** The interceptor calls this when it has reported a request failure itself. */
export const markServerErrorShown = (): void => { serverErrorAt = Date.now(); };

/**
 * The antd message instance from <NotifyHost />. It comes from `message.useMessage`, not the static
 * `message.*` (which the bridge re-routes here), so it reads the ConfigProvider theme and cannot loop.
 * Calls made before the host mounts wait in `pending`.
 */
let api: MessageInstance | null = null;
const pending: Array<(m: MessageInstance) => void> = [];
const run = (fn: (m: MessageInstance) => void): void => { if (api) fn(api); else pending.push(fn); };

/**
 * Toasts speak the standard status colours (green / red / amber / blue), whatever the brand theme
 * maps them to elsewhere - the app theme sets colorSuccess to the brand purple, which made a
 * success toast look like an info one. Nested, so dark mode, RTL and fonts still come from above.
 */
const STATUS_THEME: ThemeConfig = {
  token: {
    colorSuccess: tokens.color.success,
    colorError: tokens.color.danger,
    colorWarning: tokens.color.warning,
    colorInfo: tokens.color.info,
  },
};

/** Mount once, inside the antd ConfigProvider. Renders antd's own message holder. */
export const NotifyHost: React.FC = () => {
  const [instance, holder] = message.useMessage({ maxCount: 3, top: 16 });
  useEffect(() => {
    api = instance;
    pending.splice(0).forEach((fn) => fn(instance));
    return () => { if (api === instance) api = null; };
  }, [instance]);
  return <ConfigProvider theme={STATUS_THEME}>{holder}</ConfigProvider>;
};

const plain = (node: React.ReactNode): string =>
  typeof node === 'string' || typeof node === 'number' ? String(node) : '';

/** A server body passed straight in (`notify.error(e.response.data)`) would crash React: use its text. */
const asNode = (v: unknown): React.ReactNode => {
  if (!v || typeof v !== 'object' || React.isValidElement(v) || Array.isArray(v)) return v as React.ReactNode;
  const o = v as { message?: unknown; title?: unknown; error?: unknown };
  const text = [o.message, o.title, o.error].find((x) => typeof x === 'string');
  return (text as string | undefined) ?? '';
};

export const show = (kind: NotifyKind, content: React.ReactNode, opts: NotifyOptions = {}): string => {
  let title = asNode(content);
  let description = opts.description;
  if (kind === 'error') {
    if (!opts.id && Date.now() - serverErrorAt < SERVER_ERROR_QUIET_MS) return '';
    if (typeof title === 'string') title = userFacingError(title);
    if (typeof description === 'string') description = isTechnicalMessage(description) || libraryText(description) ? undefined : description;
  }
  if (title == null || title === '') return '';
  if (plain(description) && plain(description) === plain(title)) description = undefined;

  const body: React.ReactNode = description
    ? <span role={kind === 'error' ? 'alert' : 'status'}>
        <Typography.Text strong>{title}</Typography.Text>
        <br />
        <Typography.Text type="secondary">{description}</Typography.Text>
      </span>
    : title;
  const id = opts.id ?? `${kind}:${plain(title) || Math.random()}`;
  const ms = opts.duration === 0 ? Infinity : opts.duration ?? DEFAULT_MS[kind] + (description ? 1500 : 0);
  // antd durations are seconds, 0 = until dismissed.
  const duration = Number.isFinite(ms) ? ms / 1000 : 0;

  run((m) => { m.open({ type: kind, key: id, content: body, duration, icon: opts.icon }); });
  return id;
};

const dismiss = (id?: string): void => run((m) => m.destroy(id));

export const notify = {
  success: (c: React.ReactNode, o?: NotifyOptions) => show('success', c, o),
  error: (c: React.ReactNode, o?: NotifyOptions) => show('error', c, o),
  info: (c: React.ReactNode, o?: NotifyOptions) => show('info', c, o),
  warning: (c: React.ReactNode, o?: NotifyOptions) => show('warning', c, o),
  loading: (c: React.ReactNode, o?: NotifyOptions) => show('loading', c, o),
  dismiss,
};

export default notify;
